import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { buyerService } from '../services/apiService';
import ErrorBoundary from '../components/ErrorBoundary';
import BuyerListingDetailsModal from '../components/Buyer/BuyerListingDetailsModal';

const BuyerContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1.5rem 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
  }
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto 2.5rem;
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  color: #000000;
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -100px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
    border-radius: 50%;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 70%);
    border-radius: 50%;
  }

  h1 {
    margin: 0 0 0.75rem 0;
    color: #000000;
    font-size: 2.2rem;
    font-weight: 700;
    letter-spacing: -0.5px;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      font-size: 1.6rem;
    }

    @media (max-width: 480px) {
      font-size: 1.4rem;
      margin: 0 0 0.5rem 0;
    }
  }

  p {
    margin: 0.5rem 0;
    color: #4b5563;
    font-size: 1rem;
    font-weight: 500;
    position: relative;
    z-index: 1;

    @media (max-width: 480px) {
      font-size: 0.9rem;
      margin: 0.3rem 0;
    }
  }

  .subtitle {
    font-size: 0.95rem;
    opacity: 0.9;
    margin-top: 1rem;

    @media (max-width: 480px) {
      font-size: 0.85rem;
      margin-top: 0.75rem;
    }
  }
`;

const FilterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto 3rem;
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.15);

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin: 0 auto 2rem;
  }

  input, select {
    padding: 0.85rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: #f9f9f9;

    &:focus {
      outline: none;
      border-color: #667eea;
      background: white;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    &::placeholder {
      color: #aaa;
    }

    @media (max-width: 480px) {
      padding: 0.75rem 0.9rem;
      font-size: 0.9rem;
    }
  }

  button {
    padding: 0.6rem 1.2rem;
    background: #1e293b;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    &:hover {
      background: #0f172a;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    &:active {
      transform: translateY(0);
    }

    @media (max-width: 480px) {
      padding: 0.65rem 1.2rem;
      font-size: 0.8rem;
    }
  }
`;

const SectionDivider = styled.div`
  max-width: 1200px;
  margin: 3rem auto;
  text-align: center;

  h2 {
    font-size: 1.8rem;
    color: #000000;
    margin: 0;
    position: relative;

    &:after {
      content: '';
      display: block;
      width: 60px;
      height: 2px;
      background: #1e293b;
      margin: 0.5rem auto 0;
      border-radius: 1px;
    }
  }
`;

const ListingsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 0.75rem;
  }
`;

const ListingCard = styled.div`
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(102, 126, 234, 0.08);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.15);
  }

  @media (max-width: 480px) {
    border-radius: 12px;

    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const CardHeader = styled.div`
  background: #1e293b;
  color: white;
  padding: 1.25rem;
  position: relative;

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.15rem;
    font-weight: 800;
    letter-spacing: -0.3px;
  }

  p {
    margin: 0.3rem 0;
    font-size: 0.9rem;
    opacity: 0.95;
    font-weight: 500;
  }

  .badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.25);
    padding: 0.35rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    margin-top: 0.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2px;
  }

  @media (max-width: 480px) {
    padding: 1rem;

    h3 {
      font-size: 1.05rem;
    }

    p {
      font-size: 0.85rem;
    }
  }
`;

const CardBody = styled.div`
  padding: 1rem 1.2rem;
  flex: 1;
  overflow: hidden;

  p {
    margin: 0.4rem 0;
    color: #666;
    font-size: 0.9rem;
    line-height: 1.3;

    strong {
      color: #2c3e50;
      font-weight: 600;
    }

    a {
      color: #667eea;
      text-decoration: none;
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .price {
    font-size: 1.3rem;
    color: #667eea;
    font-weight: 800;
    margin: 1rem 0 0.5rem 0;
    letter-spacing: -0.3px;

    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }

  .farmer-info {
    background: linear-gradient(135deg, #f8f9ff 0%, #f0eff5 100%);
    padding: 0.8rem;
    border-radius: 8px;
    margin: 0.8rem 0 0 0;
    border-left: 4px solid #667eea;
  }
`;

const CardFooter = styled.div`
  padding: 1rem 1.2rem;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  gap: 0.8rem;
  background: linear-gradient(to bottom, #fafbfc, #f5f7fb);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.6rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1rem;
    gap: 0.5rem;
  }

  button {
    flex: 1;
    padding: 0.7rem 0.9rem;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.3s ease;
    min-height: 44px;

    &.view-details {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(102, 126, 234, 0.2);
      }
    }

    &.contact {
      background: white;
      color: #667eea;
      border: 2px solid #667eea;
      font-weight: 700;

      &:hover {
        background: #667eea;
        color: white;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      }
    }

    @media (max-width: 480px) {
      padding: 0.6rem 0.8rem;
      font-size: 0.8rem;
      min-height: 40px;
    }
  }
`;

const EmptyState = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border: 2px dashed rgba(102, 126, 234, 0.2);
  margin: 0 1rem;

  h3 {
    color: #2c3e50;
    margin-bottom: 0.75rem;
    font-size: 1.4rem;
    font-weight: 700;
  }

  p {
    color: #667eea;
    font-size: 1rem;
    font-weight: 500;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    margin: 0 0.75rem;

    h3 {
      font-size: 1.2rem;
    }

    p {
      font-size: 0.9rem;
    }
  }
`;

const LoadingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  margin: 0 1rem;

  p {
    color: #667eea;
    font-size: 1rem;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    margin: 0 0.75rem;

    p {
      font-size: 0.9rem;
    }
  }
`;

const BuyerListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    cropType: '',
    state: '',
    region: ''
  });
  const [selectedListing, setSelectedListing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const buyerId = parseInt(localStorage.getItem('userId'));
  const buyerName = localStorage.getItem('userName') || 'Buyer';

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    setLoading(true);
    try {
      const data = await buyerService.getListings(
        filters.cropType || undefined,
        filters.state || undefined,
        filters.region || undefined
      );
      setListings(data || []);
    } catch (error) {
      console.error('Error loading listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSearch = () => {
    loadListings();
  };

  const handleContactSeller = (listing) => {
    // Navigate to messaging with seller ID as query param
    navigate(`/messages?sellerId=${listing.sellerId}&sellerName=${encodeURIComponent(listing.sellerName || 'Seller')}&cropType=${encodeURIComponent(listing.cropType)}`);
  };

  const handleViewDetails = (listing) => {
    setSelectedListing(listing);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedListing(null);
  };

  const handleOrderSeller = (listing) => {
    alert(`Place order for listing: ${listing.cropType}`);
  };

  return (
    <BuyerContainer>
      <Header>
        <h1>🎯 Welcome, {buyerName}!</h1>
        <p>Browse and purchase directly from local farmers</p>
        <p className="subtitle">All listings below are verified and approved by our admin team</p>
      </Header>

      <SectionDivider>
        <h2>Available Products</h2>
      </SectionDivider>

      <FilterContainer>
        <input
          type="text"
          name="cropType"
          placeholder="Search crop type (e.g. Rice, Wheat)"
          value={filters.cropType}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="state"
          placeholder="State"
          value={filters.state}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="region"
          placeholder="Region"
          value={filters.region}
          onChange={handleFilterChange}
        />
        <button onClick={handleSearch}>🔍 Search Listings</button>
      </FilterContainer>

      {loading ? (
        <LoadingContainer>
          <p>⏳ Loading available crops...</p>
        </LoadingContainer>
      ) : listings.length === 0 ? (
        <EmptyState>
          <h3>No listings found</h3>
          <p>Try adjusting your search criteria or check back later</p>
        </EmptyState>
      ) : (
        <ListingsGrid>
          {listings.map(listing => (
            <ListingCard key={listing.listingId}>
              <CardHeader>
                <h3>🌾 {listing.cropType}</h3>
                <p><strong>Variety:</strong> {listing.breedVariety}</p>
                <p><strong>📍 Location:</strong> {listing.region}, {listing.state}</p>
                <span className="badge">Verified Seller</span>
              </CardHeader>

              <CardBody>
                <p><strong>Details:</strong> {listing.farmingDetails}</p>
                <p><strong>Available Quantity:</strong> {listing.harvestQuantityInKg} kg</p>
                <p><strong>Minimum Order:</strong> {listing.minimumOrderQuantity} kg</p>
                
                <div className="price">₹{listing.basePrice}/kg</div>

                <div className="farmer-info">
                  <p><strong>🌱 Cultivation Area:</strong> {listing.cultivationAreaInAcres} acres</p>
                  {listing.certificationUrl && (
                    <p>
                      <strong>📜 Certification:</strong> <a href={listing.certificationUrl} target="_blank" rel="noopener noreferrer">View Document</a>
                    </p>
                  )}
                </div>
              </CardBody>

              <CardFooter>
                <button className="contact" onClick={() => handleContactSeller(listing)}>
                  💬 Message
                </button>
                <button className="view-details" onClick={() => handleViewDetails(listing)}>
                  👁️ View Details
                </button>
              </CardFooter>
            </ListingCard>
          ))}
        </ListingsGrid>
      )}

    {showModal && selectedListing && (
      <BuyerListingDetailsModal 
        listing={selectedListing} 
        onClose={handleCloseModal}
        onContact={handleContactSeller}
        onOrder={handleOrderSeller}
      />
    )}
    </BuyerContainer>
  );
};

export default BuyerListings;
